import { IQuote, IFormStep, InsuranceQuoteType, getInsuranceTypeDisplayName, QuoteTypeCodes } from '@/interfaces';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { formatCurrency } from '@/utils/text-transforms';
import { PaymentTypes } from '@/enums/payment-types.enum';
import { PaymentFrequency } from '@/enums/payment-frequency';
import { format } from 'date-fns';
import { STANDARD_DATE_FORMAT } from '@/constants/formats';
import { AlertTypes } from '@/enums/alert-types-enum';

const DiscountReason_Key_Prefix = 'DR_';

export enum Bookmarks {
  email = 'email',
  amountPaid = 'amountPaid',
  paymentReferenceNumber = 'paymentReferenceNumber',
  paymentReceiptNumber = 'paymentReceiptNumber',
  policyReferenceNumber = 'policyReferenceNumber',
  paymentAdminFee = 'paymentAdminFee',
  dueDate = 'dueDate',
  deposit = 'deposit',
  instalment = 'instalment',
  total = 'total',
  discountReasons = 'discountReasons',
  monthlyPayment = 'monthlyPayment',
  annualPayment = 'annualPayment',
  firstPayment = 'firstPayment',
  plural = 'plural',
  numberReferenceType = 'numberReferenceType',
}

export const getBasicConfirmationHeaderContent = (step: IFormStep, amount: number, reference: string) => {
  const summaryPaymentAmount = replaceBookmark(
    Bookmarks.amountPaid,
    step.formInputs?.confirmationHeader?.paymentAmountSummary?.value,
    formatCurrency(amount, 2)
  );
  return {
    alert: {
      text: step.formInputs?.confirmationHeader?.alertText?.value,
      alertType: AlertTypes.success,
      showIcon: true,
    },
    title: step.formInputs?.confirmationHeader?.title?.value,
    subTitle: step.formInputs?.confirmationHeader?.subTitle?.value,
    totalAmountSummary: summaryPaymentAmount,
    receiptNumber: reference ? `Receipt number: ${reference}` : null,
  };
};

export const getConfirmationHeaderContent = (
  step: IFormStep,
  quote: Partial<IQuote>,
  payByTheMonthFee: number,
  dictionary: any
) => {
  const summaryPaymentAmount = getSummaryPaymentAmount(step, quote, payByTheMonthFee, dictionary);
  const policyOrReferenceNumber = getPolicyOrReferenceNumber(step, quote);
  const receiptNumber = getPaymentReceiptNumber(step, quote);
  const alertType = getAlertType(quote);

  if (quote.paymentType === PaymentTypes.PayLater) {
    return {
      alert: {
        text: step.formInputs?.confirmationHeaderPaylater?.alertText?.value,
        alertType: alertType,
        showIcon: true,
      },
      icon: { fileName: getConfirmationHeaderIcon(quote), alt: 'Confirmation Icon', rounded: false },
      title: step.formInputs?.confirmationHeaderPaylater?.title?.value,
      subTitle: getDisplayInsuranceName(step, quote, dictionary),
      totalAmountSummary: summaryPaymentAmount,
      policyOrReferenceNumber: policyOrReferenceNumber,
      receiptNumber: receiptNumber,
      paymentReferenceNumber: getPaymentReferenceNumberDetails(step, quote)?.content,
    };
  } else if (quote.paymentFrequency === PaymentFrequency.Annually) {
    return {
      alert: {
        text:
          annualCreditCardPaymentFailure(quote) && bindSuccessful(quote)
            ? step.formInputs?.paymentFailedHeaderText?.label?.value
            : step.formInputs?.confirmationHeaderPayNowAnnual?.alertText?.value,
        alertType: alertType,
        showIcon: true,
      },
      icon: { fileName: getConfirmationHeaderIcon(quote), alt: 'Confirmation Icon', rounded: false },
      title: getAnnualTitleText(
        quote,
        step.formInputs?.confirmationHeaderPayNowAnnual?.title?.value,
        step.formInputs?.confirmationHeaderPayNowAnnual?.altTitle?.value
      ),
      subTitle: getDisplayInsuranceName(step, quote, dictionary),
      totalAmountSummary: summaryPaymentAmount,
      policyOrReferenceNumber: policyOrReferenceNumber,
      receiptNumber: receiptNumber,
      paymentReferenceNumber: getVisibleReceipt(quote) ? '' : getPaymentReferenceNumberDetails(step, quote)?.content,
    };
  } else if (quote.paymentFrequency === PaymentFrequency.Monthly) {
    return {
      alert: {
        text: step.formInputs?.confirmationHeaderPayNowMonthly?.alertText?.value,
        alertType: alertType,
        showIcon: true,
      },
      icon: { fileName: getConfirmationHeaderIcon(quote), alt: 'Confirmation Icon', rounded: false },
      title: step.formInputs?.confirmationHeaderPayNowMonthly?.title?.value,
      subTitle: getDisplayInsuranceName(step, quote, dictionary),
      totalAmountSummary: summaryPaymentAmount,
      policyOrReferenceNumber: policyOrReferenceNumber,
      receiptNumber: receiptNumber,
    };
  }
};

export const getAnnualTitleText = (quote: Partial<IQuote>, defaultTitle: string, altTitle: string) => {
  const isPayLater = quote.paymentType === PaymentTypes.PayLater;
  const showThankyouChoosingRACQ = isPayLater || (annualCreditCardPaymentFailure(quote) && bindSuccessful(quote));

  if (showThankyouChoosingRACQ) {
    return altTitle;
  }
  return defaultTitle;
};

export const annualCreditCardPaymentFailure = (quote: Partial<IQuote>) => {
  const failedPaymentCardNoReceipt =
    quote.paymentFrequency === PaymentFrequency.Annually &&
    quote.paymentType === PaymentTypes.CreditCard &&
    !paymentSuccessful(quote);
  return failedPaymentCardNoReceipt;
};

export const getConfirmationHeaderIcon = (quote: Partial<IQuote>) => {
  let icon = '';

  const quoteType = quote.quoteType;
  const isPayLater = quote.paymentType === PaymentTypes.PayLater;
  const showTicked = !isPayLater && !(annualCreditCardPaymentFailure(quote) && bindSuccessful(quote));

  switch (quoteType) {
    case InsuranceQuoteType.HomeAndContents:
      icon = showTicked ? 'home-contents-ticked.svg' : 'home-contents-no-tick.svg';
      break;
    case InsuranceQuoteType.Home:
      icon = showTicked ? 'home-ticked.svg' : 'home-no-tick.svg';
      break;
    case InsuranceQuoteType.Contents:
      icon = showTicked ? 'content-ticked.svg' : 'content-no-tick.svg';
      break;
    case InsuranceQuoteType.PetComp:
      icon = showTicked ? 'pet-ticked.svg' : 'pet-paw.svg';
      break;
    case InsuranceQuoteType.MotorComprehensive:
    case InsuranceQuoteType.MotorComprehensiveOther:
    case InsuranceQuoteType.MotorFireTheftAndThirdPartyLiability:
    case InsuranceQuoteType.MotorFireTheftAndThirdPartyLiabilityOther:
    case InsuranceQuoteType.MotorThirdPartyLiability:
    case InsuranceQuoteType.MotorThirdPartyLiabilityOther:
      icon = showTicked ? 'car-ticked.svg' : 'car.svg';
      break;
  }

  if (!icon) {
    icon = 'home-contents-no-tick.svg';
  }

  return require(`@/assets/icons/${icon}`);
};

export const getHomeInsuranceTypeIcon = (quote: Partial<IQuote>) => {
  let icon = 'home-contents-no-tick.svg';

  switch (quote.quoteType) {
    case InsuranceQuoteType.HomeAndContents:
      icon = 'home-contents-no-tick.svg';
      break;
    case InsuranceQuoteType.Home:
      icon = 'home-no-tick.svg';
      break;
    case InsuranceQuoteType.Contents:
      icon = 'content-no-tick.svg';
      break;
  }

  return require(`@/assets/icons/${icon}`);
};

export const bindSuccessful = (quote: Partial<IQuote>) => quote?.policyReference && quote.policyReference?.length > 0;
export const paymentSuccessful = (quote: Partial<IQuote>) => (quote.paymentReceiptNumber?.length ?? 0) > 0;

const getAlertType = (quote: Partial<IQuote>): AlertTypes => {
  if (quote.paymentType === PaymentTypes.PayLater) {
    return AlertTypes.info;
  } else if (
    quote.paymentFrequency == PaymentFrequency.Annually &&
    quote.paymentType === PaymentTypes.CreditCard &&
    !getVisibleReceipt(quote) &&
    bindSuccessful(quote)
  ) {
    return AlertTypes.warning;
  } else {
    return AlertTypes.success;
  }
};

const getDisplayInsuranceName = (step: IFormStep, quote: Partial<IQuote>, dictionary: any) => {
  if (!quote.quoteType || !step?.formInputs?.confirmationHeaderPaylater) return;
  let fillBookmark = step.formInputs.confirmationHeaderPaylater.subTitle.value;
  fillBookmark = fillBookmark.replaceAll(
    '{{insuranceTypeDisplayName}}',
    getInsuranceTypeDisplayName(dictionary, QuoteTypeCodes[quote.quoteType])
  );
  return fillBookmark;
};

export const getHighlights = (step: IFormStep, quote: Partial<IQuote>, isAutoRenewalPreferenceEnabled: boolean) => {
  const highlights = [];
  const isPayLater = quote.paymentType === PaymentTypes.PayLater;
  const isAnnualBankAccountPayments =
    quote.paymentType === PaymentTypes.BankAccount && quote.paymentFrequency == PaymentFrequency.Annually;
  const isMonthlyCreditOrBankAccountPayments =
    (quote.paymentType === PaymentTypes.CreditCard || quote.paymentType === PaymentTypes.BankAccount) &&
    quote.paymentFrequency == PaymentFrequency.Monthly;

  if (
    quote.paymentFrequency === PaymentFrequency.Annually &&
    quote.paymentType === PaymentTypes.CreditCard &&
    !quote.renewalPreference &&
    paymentSuccessful(quote)
  ) {
    highlights.push({ content: step.formInputs?.confirmationBody?.highlightCoverGranted?.fields?.label?.value });
  } else if (
    (quote.paymentType === PaymentTypes.CreditCard && !paymentSuccessful(quote)) ||
    quote.paymentFrequency === PaymentFrequency.Monthly ||
    isPayLater ||
    (quote.paymentFrequency === PaymentFrequency.Annually &&
      quote.paymentType === PaymentTypes.BankAccount &&
      !quote.renewalPreference)
  ) {
    highlights.push({
      content: step.formInputs?.confirmationBody?.highlightCoverGrantedConditionalCover?.fields?.label?.value,
    });
  } else if (
    quote.paymentFrequency === PaymentFrequency.Annually &&
    quote.paymentType === PaymentTypes.BankAccount &&
    quote.renewalPreference
  ) {
    highlights.push({
      content: step.formInputs?.confirmationBody?.highlightCoverGrantedPendingPayment?.fields?.label?.value,
    });
  }

  highlights.push({
    content: replaceBookmark(
      Bookmarks.email,
      step.formInputs?.confirmationBody?.highlightConfirmationSent?.fields?.label?.value,
      quote.primaryPolicyHolderDetails?.emailAddress.value ?? ''
    ),
  });

  highlights.push({ content: step.formInputs?.confirmationBody?.highlightPolicyDocuments?.fields?.label?.value });

  if ((isAutoRenewalPreferenceEnabled && isAnnualBankAccountPayments) || isMonthlyCreditOrBankAccountPayments) {
    highlights.push({
      content: quote.renewalPreference
        ? step.formInputs?.confirmationBody?.highlightAutoRenewOn?.fields?.label?.value
        : step.formInputs?.confirmationBody?.highlightAutoRenewOff?.fields?.label?.value,
    });
  }

  return highlights;
};

export const getFirstPaymentContent = (step: IFormStep, quote: Partial<IQuote>) => {
  let content = '';

  const bindWasSuccessful = bindSuccessful(quote);
  const validPaymentType =
    quote.paymentType === PaymentTypes.CreditCard || quote.paymentType === PaymentTypes.BankAccount;

  if (validPaymentType && quote.paymentFrequency === PaymentFrequency.Monthly) {
    if (bindWasSuccessful) {
      content = step.formInputs?.confirmationBody?.firstPaymentPolicyBoundMonthlyInformationBox?.fields?.content?.value;
    } else if (!bindWasSuccessful) {
      content =
        step.formInputs?.confirmationBody?.firstPaymentPolicyNotBoundMonthlyInformationBox?.fields?.content?.value;
    }
  } else if (
    !bindWasSuccessful &&
    quote.paymentType === PaymentTypes.CreditCard &&
    quote.paymentFrequency === PaymentFrequency.Annually
  ) {
    content = step.formInputs?.confirmationBody?.firstPaymentPolicyNotBoundAnnualInformationBox?.fields?.content?.value;
  }

  if (content) {
    content = replaceBookmark(
      Bookmarks.firstPayment,
      content,
      formatCurrency(quote.insuranceQuoteSummary?.monthlyDeposit, 2) ?? ''
    );

    content = replaceBookmark(
      Bookmarks.monthlyPayment,
      content,
      formatCurrency(quote.insuranceQuoteSummary?.monthlyInstalment, 2)
    );

    content = replaceBookmark(
      Bookmarks.annualPayment,
      content,
      formatCurrency(quote.insuranceQuoteSummary?.bestPremium, 2)
    );
  }

  return { show: content && content.length > 0, content: content };
};

export const getPolicyOrReferenceNumber = (step: IFormStep, quote: Partial<IQuote>) => {
  const useReference = quote.policyReference ? quote.policyReference : quote.reference;
  if (!useReference) return '';

  const bindWasSuccessful = bindSuccessful(quote);

  let content = step.formInputs?.confirmationBody?.policyReferenceNumber?.fields?.label?.value;
  const numberReferenceType = bindWasSuccessful ? 'Policy' : 'Reference';

  content = replaceBookmark(Bookmarks.numberReferenceType, content, numberReferenceType);
  content = replaceBookmark(Bookmarks.policyReferenceNumber, content, useReference);
  content = replaceBookmark(Bookmarks.paymentReferenceNumber, content, useReference);

  return content;
};

export const getPaymentReceiptNumber = (step: IFormStep, quote: Partial<IQuote>) => {
  const visibleReceipt = getVisibleReceipt(quote);
  if (!visibleReceipt) return '';

  return replaceBookmark(
    Bookmarks.paymentReceiptNumber,
    step.formInputs?.confirmationBody?.paymentReceiptNumber?.fields?.label?.value,
    visibleReceipt ?? ''
  );
};

export const getVisibleReceipt = (quote: Partial<IQuote>) => {
  if (
    bindSuccessful(quote) &&
    quote.paymentReceiptNumber &&
    quote.paymentFrequency === PaymentFrequency.Annually &&
    (quote.paymentType === PaymentTypes.CreditCard || quote.paymentType === PaymentTypes.BankAccount)
  ) {
    return quote.paymentReceiptNumber;
  }
  return '';
};

export const getPaymentReferenceNumberDetails = (step: IFormStep, quote: Partial<IQuote>) => {
  if (quote.paymentType === PaymentTypes.PayLater || (annualCreditCardPaymentFailure(quote) && bindSuccessful(quote))) {
    const content = replaceBookmark(
      Bookmarks.paymentReferenceNumber,
      step.formInputs?.confirmationBody?.paymentReferenceNumber?.fields?.label?.value,
      quote.paymentReferenceNumber ??
        step.formInputs?.confirmationBody?.paymentReceiptNumberAlternateText?.fields?.label?.value
    );
    return { content: content, showForPaymentMethodsOnly: quote.paymentReferenceNumber === undefined };
  }
  return { content: '' };
};

export const getSummaryPaymentAmount = (
  step: IFormStep,
  quote: Partial<IQuote>,
  payByTheMonthFee: number,
  dictionary: Record<string, string>
) => {
  let content = '';
  const isPayLater = quote.paymentType === PaymentTypes.PayLater;
  let amountToDisplay = 0;

  if (isPayLater || (annualCreditCardPaymentFailure(quote) && bindSuccessful(quote))) {
    content = step.formInputs?.confirmationHeaderPaylater?.paymentAmountSummary?.value;
    amountToDisplay = quote.insuranceQuoteSummary?.monthlyDeposit ?? 0;
  } else if (quote.paymentFrequency == PaymentFrequency.Annually) {
    content = step.formInputs?.confirmationHeaderPayNowAnnual?.paymentAmountSummary?.value;
    amountToDisplay = quote.insuranceQuoteSummary?.bestPremium ?? 0;
  } else if (quote.paymentFrequency == PaymentFrequency.Monthly) {
    content = step.formInputs?.confirmationHeaderPayNowMonthly?.paymentAmountSummary?.value;
    amountToDisplay = quote.insuranceQuoteSummary?.monthlyDeposit ?? 0;
  }

  content = replaceBookmarksSpecifyingAmount(content, quote, dictionary, amountToDisplay, payByTheMonthFee);

  return content;
};

export const replaceBookmarksSpecifyingAmount = (
  content: string,
  quote: Partial<IQuote>,
  dictionary: Record<string, string>,
  amountToDisplay: number,
  payByTheMonthFee: number
) => {
  const updateContent = replaceBookmark(Bookmarks.amountPaid, content, formatCurrency(amountToDisplay, 2));

  return replaceBookmarks(updateContent, quote, payByTheMonthFee, dictionary);
};

export const replaceBookmarks = (
  content: string,
  quote: Partial<IQuote>,
  payByTheMonthFee: number,
  dictionary: Record<string, string>
) => {
  const isPayLater = quote.paymentType === PaymentTypes.PayLater;
  const amountPaid = isPayLater
    ? quote.insuranceQuoteSummary?.monthlyDeposit
    : quote.insuranceQuoteSummary?.bestPremium;
  const dueDate = quote.paymentDueDate ? quote.paymentDueDate : '';

  let updateContent = replaceBookmark(Bookmarks.amountPaid, content, formatCurrency(amountPaid, 2));

  if (dueDate) {
    updateContent = replaceBookmark(Bookmarks.dueDate, updateContent, format(new Date(dueDate), STANDARD_DATE_FORMAT));
  }

  if (payByTheMonthFee) {
    updateContent = replaceBookmark(Bookmarks.paymentAdminFee, updateContent, formatCurrency(payByTheMonthFee, 2));
  }

  const discountReasons = generateDiscountReasons(quote, dictionary);
  updateContent = replaceBookmark(Bookmarks.discountReasons, updateContent, discountReasons.content);
  updateContent = replaceBookmark(Bookmarks.plural, updateContent, discountReasons.plural);

  return updateContent;
};

export const getGoodNewsDiscount = (
  step: IFormStep,
  quote: Partial<IQuote>,
  payByTheMonthFee: number,
  dictionary: Record<string, string>
) => {
  let content = '';
  const isPayLater = quote.paymentType === PaymentTypes.PayLater;
  const bindWasSuccessful = bindSuccessful(quote);
  const payByMonthFeeWaived = quote.ruleCalculations?.payByTheMonthFeeWaived;
  const premiumLowerThanQuoted = quote?.ruleCalculations?.premiumLowerThanQuoted;
  let hasPaymentAdminFeeBookmark = false;

  if (isPayLater) {
    if (bindWasSuccessful && premiumLowerThanQuoted) {
      content = step.formInputs?.confirmationBody?.goodNewsLowerPremiumAnnualPayLater?.fields?.label?.value;
      hasPaymentAdminFeeBookmark = true;
    }
  } else if (quote.paymentFrequency == PaymentFrequency.Annually) {
    if (
      bindWasSuccessful &&
      premiumLowerThanQuoted &&
      (quote.paymentType === PaymentTypes.CreditCard || quote.paymentType === PaymentTypes.BankAccount)
    ) {
      content = step.formInputs?.confirmationBody?.goodNewsLowerPremiumAnnualPayNow?.fields?.label?.value;
    }
  } else if (quote.paymentFrequency == PaymentFrequency.Monthly) {
    if (bindWasSuccessful && !payByMonthFeeWaived && premiumLowerThanQuoted) {
      content = step.formInputs?.confirmationBody?.goodNewsLowerPremiumMonthlyPayNow?.fields?.label?.value;
    } else if (bindWasSuccessful && payByMonthFeeWaived && !premiumLowerThanQuoted) {
      content = step.formInputs?.confirmationBody?.goodNewsFeeWaiverMonthlyPayNow?.fields?.label?.value;
      hasPaymentAdminFeeBookmark = true;
    } else if (
      bindWasSuccessful &&
      payByMonthFeeWaived &&
      premiumLowerThanQuoted &&
      (quote.paymentType === PaymentTypes.CreditCard || quote.paymentType === PaymentTypes.BankAccount)
    ) {
      content = step.formInputs?.confirmationBody?.goodNewsLowerPremiumFeeWaiverMonthlyPayNow?.fields?.label?.value;
      hasPaymentAdminFeeBookmark = true;
    }
  }

  if (content) {
    content = replaceBookmarks(content, quote, payByTheMonthFee, dictionary);
  }

  const hasAdminFeeBookmarkAndValidFee = hasPaymentAdminFeeBookmark && payByTheMonthFee > 0;

  return {
    show: (content.length > 0 && !hasPaymentAdminFeeBookmark) || hasAdminFeeBookmarkAndValidFee,
    content: content,
  };
};

export const getMonthlyDeductions = (step: IFormStep, quote: Partial<IQuote>) => {
  if (quote.paymentType !== PaymentTypes.CreditCard || quote.paymentFrequency == PaymentFrequency.Annually) return '';

  return step.formInputs?.confirmationBody?.paymentDeductionInfoMonthly?.fields?.label?.value;
};

const generateDiscountReasons = (
  quote: Partial<IQuote>,
  dictionary: Record<string, string>
): { content: string; plural: string } => {
  if (!quote.ruleCalculations?.discountDifferences || quote.ruleCalculations?.discountDifferences.length === 0)
    return { content: '', plural: '' };

  //create discount difference dictionary key prefix to match with.
  const discountDifferencesDictKeys = quote.ruleCalculations?.discountDifferences.map(function (d: any) {
    return `${DiscountReason_Key_Prefix}${d.discountType}`;
  });

  let reasonText = '';

  //Filter dictionary to Discount Reasons prefixed with DR_.
  for (const [key, value] of Object.entries(dictionary).filter((x) => x[0].indexOf(DiscountReason_Key_Prefix) > -1)) {
    if (discountDifferencesDictKeys.findIndex((x) => key.indexOf(x) > -1) > -1) {
      reasonText = reasonText + (reasonText.length === 0 ? value?.trim() : ` and ${value?.trim()}`);
    }
  }

  const plural = quote.ruleCalculations?.discountDifferences?.length > 1 ? 's' : '';
  return { content: reasonText, plural: plural };
};
